import { palette } from './palette';

const { base, blue, green, grey, purple, red, yellow } = palette.primitives;

/**
 * Alpha-based utilities not present in the design system.
 * Used for very subtle hover/focus/selected states where the palette's
 * `*0` (50% alpha) tints would be too strong.
 */
const alpha = {
  black04: 'rgba(0, 0, 0, 0.04)',
  black08: 'rgba(0, 0, 0, 0.08)',
  accent08: 'rgba(122, 12, 255, 0.08)',
  accent20: 'rgba(122, 12, 255, 0.20)'
} as const;

/**
 * Semantic color tokens. Components import these — NOT the raw palette.
 *
 * Every visual decision (panel bg, accent, clip variants) maps to one of these
 * names. When the Figma palette changes, only ./palette.ts is touched.
 */
export const colors = {
  // Surfaces
  panelBg: purple.purple0,
  toolbarBg: purple.purple0,
  sidebarBg: base.white,
  trackAreaBg: base.white,
  surfaceElevated: base.white,

  // Borders
  border: grey.grey100,
  borderSubtle: alpha.black08,

  // Accent (brand)
  accent: purple.purple500,
  accentHover: purple.purple600,
  accentMuted: alpha.accent08,
  accentMutedHover: alpha.accent20,
  onAccent: base.white,

  // Text
  textPrimary: grey.grey900,
  textSecondary: grey.grey500,
  textMuted: grey.grey300,
  textOnAccent: base.white,

  // Row states
  rowBg: 'transparent',
  rowHover: alpha.black04,
  rowSelected: alpha.accent08,
  rowSelectedAccent: purple.purple500,

  // Buttons (ghost / icon)
  btnBg: alpha.black04,
  btnHover: alpha.black08,
  btnDisabled: grey.grey200,

  // Clip variants — background + foreground per clip kind
  clipTextBg: purple.purple200,
  clipTextFg: base.white,
  clipSubtitleBg: base.white,
  clipSubtitleFg: grey.grey900,
  clipSubtitleBorder: grey.grey100,
  clipMediaBg: yellow.yellow300,
  clipMediaFg: grey.grey400,
  clipVideoBg: blue.blue200,
  clipVideoFg: base.white,
  clipAudioBg: green.green200,
  clipAudioFg: grey.grey900,
  clipShapeBg: purple.purple300,
  clipShapeFg: base.white,
  clipSfxBg: red.red300,
  clipSfxFg: grey.grey900,
  clipDefaultBg: grey.grey200,
  clipDefaultFg: grey.grey900,

  // Playhead
  playhead: purple.purple500,
  playheadTrack: purple.purple500,
  playheadRail: grey.grey100,
  playheadHandle: purple.purple500
} as const;

export type ColorToken = keyof typeof colors;
